import { CampaignConfigDto } from '@coretava/api/ecommerce-service';
import { LinkReferralDto, ReferralDto } from '@coretava/api/events-service';
import { calculateBoosterEndDate, formatCampaignReward, formatRefereeReward } from '@gamiphy/loyalty-station-desktop/components/cashback/utils';
import InviteOptionsPopup from '@gamiphy/loyalty-station-desktop/components/circles/components/InviteOptionsPopup';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { ReferralsStore } from '@gamiphy/loyalty-station-desktop/store/refferals';
import { Trans } from '@gamiphy/react-i18n';
import { formatDate } from '@gamiphy/utils/date';
import {
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { addDays } from 'date-fns';
import { observer } from 'mobx-react';
import React from 'react';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import Person from '../assets/Person';
import Share from '../assets/Share';
import TwoPeople from '../assets/TwoPeople';
import Benefit from '../Benefit';

const Invite: React.FC<{campaign: CampaignConfigDto}> = ({ campaign }) => {
  const theme = useTheme();
  const [inviteOptionsOpen, setInviteOptionsOpen] = React.useState(false);
  const [linkReferral, setLinkReferral] = React.useState<LinkReferralDto | undefined>(undefined);
  const [appReferral, setAppReferral] = React.useState<ReferralDto | undefined>(undefined);

  React.useEffect(() => {
    const fetchReferrals = async () => {
      if (campaign?.booster?.id) {
        const [linkReferralResponse, appReferralResponse] = await Promise.all([
          ReferralsStore.linkReferralsApi.linkReferralsControllerGet(AppStore.app.id, campaign.booster.id),
          ReferralsStore.referralsApi.referralsControllerGet(AppStore.app.id, campaign.booster.id),
        ]);
        setLinkReferral(linkReferralResponse.data.linkReferral);
        setAppReferral(appReferralResponse.data.referral);
      }
    };

    void fetchReferrals();
  }, [AppStore.app, campaign?.booster?.id]);

  const handleClick = React.useCallback(() => {
    if (!UserStore.user?.phone) {
      UiStore.openSignupPopup();
    } else {
      setInviteOptionsOpen(true);
    }
  }, [UserStore.user?.phone]);

  if (!campaign) return null;

  return (
    <Stack spacing={2}>
      <Typography fontSize={14} fontWeight={600}>
        <Trans i18nKey="referral.tab.title" defaults="Invite your friends and everybody wins" />
      </Typography>
      <Typography fontSize={14} fontWeight={600}>
        <Trans i18nKey="referral.tab.stepsTitle" defaults="How it works:" />
      </Typography>
      <Benefit
        icon={<Share color={theme.palette.primary.main} width="100%" />}
        text={(
          <Trans
            i18nKey="campaigns.boosters.referral.referrer.invite"
            defaults="Invita a tu amigo a registrarse y realizar una compra antes del {{boosterExpiryDate}}"
            values={{
              boosterExpiryDate: formatDate(addDays(calculateBoosterEndDate(campaign), 1)),
            }}
          />
          )}
      />
      <Benefit
        icon={<Person color={theme.palette.primary.main} width="100%" />}
        text={(
          <Trans
            i18nKey="campaigns.boosters.referral.referee.reward"
            defaults="Your friend receives {{reward}} off their first purchase"
            values={{ reward: formatRefereeReward(campaign) }}
          />
          )}
      />
      <Benefit
        icon={<TwoPeople color={theme.palette.primary.main} width="100%" />}
        text={(
          <Trans
            i18nKey="campaigns.boosters.referral.referrer.reward"
            defaults="You’ll get {{reward}} off your next purchase"
            values={{ reward: formatCampaignReward(campaign) }}
          />
          )}
      />
      <Button
        variant="contained"
        fullWidth
        sx={{
          fontSize: '1.2rem',
        }}
        onClick={handleClick}
      >
        <Trans
          i18nKey="salesRep.cashback.inviteFriend"
          defaults="Invite a Friend"
        />
      </Button>
      {appReferral && (
        <InviteOptionsPopup
          open={inviteOptionsOpen}
          onChangeState={() => setInviteOptionsOpen((prevState) => !prevState)}
          appReferral={appReferral}
          linkReferral={linkReferral}
        />
      )}
    </Stack>
  );
};

export default observer(Invite);
